import { logError } from '@ptek/client-logger';
import { useWidgetContext, useWidgetEvents } from '@ptek/widget-core';
import { useCallback } from 'react';
import { WIDGET_EVENTS } from '../constants/events';
import { AmbientScribeContext } from '../../types';
import { useWidgetAuth } from '../context/AuthContext';
import { useRecordingStore } from '../stores';
import { isStandaloneMode } from '../utils/standaloneMode';

interface UseRecordingActionsReturn {
  handleSaveAndExit: (exitAction: 'logout' | 'close') => Promise<void>;
}

export const useRecordingActions = (): UseRecordingActionsReturn => {
  const { emitToHost } = useWidgetEvents();
  const { logout } = useWidgetAuth();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();

  const {
    isRecording: isCurrentlyRecording,
    isPaused,
    unifiedSessionIdEnabled,
    callStopRecording,
    callAbortRecording,
  } = useRecordingStore();

  const handleSaveAndExit = useCallback(
    async (exitAction: 'logout' | 'close') => {
      const executeExitAction = () => {
        if (exitAction === 'logout') {
          logout();
        } else if (
          exitAction === 'close' &&
          !isStandaloneMode(widgetContext.context)
        ) {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.CONFIRM_CLOSE);
        }
      };

      try {
        if (isCurrentlyRecording) {
          if (isPaused && unifiedSessionIdEnabled) {
            await callAbortRecording();
            executeExitAction();
          } else {
            await callStopRecording(() => {
              executeExitAction();
            });
          }
        } else {
          executeExitAction();
        }
      } catch (error) {
        logError('useRecordingActions', 'Failed to save and exit:', error);

        executeExitAction();
      }
    },
    [
      logout,
      emitToHost,
      widgetContext.context,
      isCurrentlyRecording,
      isPaused,
      unifiedSessionIdEnabled,
      callStopRecording,
      callAbortRecording,
    ]
  );

  return {
    handleSaveAndExit,
  };
};
